"use client";

import { ApiError } from "@punchup/utils/apiClient";
import { getApiClient } from "@punchup/utils/apiRegistry";
import React, { useState } from "react";

import { Loader2 } from "lucide-react";
import { ShowWithComedian } from "@punchup/data/legacy";
import { WAITLIST_QUERY_KEYS } from "@punchup/data/client";
import SignInSignUpForm from "../signin/SignInSignUpForm";
import { captureException } from "@sentry/nextjs";
import dayjs from "@punchup/utils/dayjs";
import { usePathname } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { useUser } from "@punchup/data/legacy/hooks";

interface WaitlistInlineFormProps {
  show: ShowWithComedian;
  ownerId: string;
  isVenue?: boolean;
  comedianSlug?: string;
  venueSlug?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const WaitlistInlineForm: React.FC<WaitlistInlineFormProps> = ({
  show,
  ownerId,
  isVenue,
  comedianSlug,
  venueSlug,
  onSuccess,
  onCancel,
}) => {
  const user = useUser();
  const isAuthenticated = !!user;
  const queryClient = useQueryClient();
  const pathname = usePathname();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");

  const buildRequestBody = () => {
    const datetime =
      typeof show.datetime === "string" ? dayjs(show.datetime) : show.datetime;
    return {
      showName:
        show.title || show.venue + (datetime ? datetime.format(" MMM D") : ""),
      receiveUpdates: true,
      showId: show.id,
      ownerId,
      authenticated: true,
      isVenue,
      email: user?.email || undefined,
      phone: user?.phone || undefined,
    };
  };

  const submitJoin = async () => {
    try {
      setLoading(true);
      setStatus("idle");

      await getApiClient()
        .post("/api/v2/user/waitlist", buildRequestBody())
        .catch((error) => {
          if (error instanceof ApiError && error.status === 409) {
            setStatus("success");
            Promise.all([
              queryClient.invalidateQueries({
                queryKey: [WAITLIST_QUERY_KEYS.perShow],
                refetchType: "active",
              }),
              queryClient.invalidateQueries({
                queryKey: [WAITLIST_QUERY_KEYS.batched],
                refetchType: "active",
              }),
            ]).then(() => {
              setTimeout(() => {
                onSuccess?.();
              }, 1500);
            });
            return;
          }
          throw error;
        });

      setStatus("success");

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [WAITLIST_QUERY_KEYS.perShow],
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: [WAITLIST_QUERY_KEYS.batched],
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: [WAITLIST_QUERY_KEYS.dashboard],
        }),
      ]);

      setTimeout(() => {
        onSuccess?.();
      }, 1500);
    } catch (error) {
      captureException(error);
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  // Called by SignInSignUpForm after a successful inline sign-in/sign-up.
  // Brief wait gives the auth session a chance to propagate before posting.
  const handleAuthComplete = async () => {
    await new Promise((resolve) => setTimeout(resolve, 100));
    await submitJoin();
  };

  if (!isAuthenticated) {
    // Redirect target for OAuth or any sign-in path that does a full page
    // reload. After landing, useAutoJoinWaitlist picks up these params and
    // fires the join automatically.
    const datetime =
      typeof show.datetime === "string" ? dayjs(show.datetime) : show.datetime;
    const showName =
      show.title || show.venue + (datetime ? datetime.format(" MMM D") : "");
    const waitlistParams = new URLSearchParams({
      joinWaitlist: "true",
      showId: show.id,
      showName,
      ownerId,
      isVenue: String(isVenue || false),
    });

    let redirectTo: string | null = null;
    if (pathname) {
      redirectTo = `${pathname}?${waitlistParams.toString()}`;
    } else if (isVenue && venueSlug) {
      redirectTo = `/v/${venueSlug}?${waitlistParams.toString()}`;
    } else if (comedianSlug) {
      redirectTo = `/${comedianSlug}?${waitlistParams.toString()}`;
    }

    if (status === "success") {
      return (
        <div className="text-center py-3 px-4 rounded-lg font-medium bg-green-500 text-white">
          Successfully Added! ✓
        </div>
      );
    }

    return (
      <SignInSignUpForm
        buttonText="Sign in to Join Waitlist"
        policyFooterType="default"
        redirectTo={redirectTo}
        handleAuthComplete={handleAuthComplete}
        variant="minimal"
      />
    );
  }

  return (
    <>
      <div className="text-center mb-6">
        <p className="text-xl font-semibold mb-2">
          You&apos;re about to join the waitlist
        </p>
        <p className="text-gray-500 text-sm">
          Join the waitlist to get information about new dates and be the first
          to grab your tickets!
        </p>
      </div>

      <div className="space-y-4">
        <button
          onClick={submitJoin}
          disabled={loading || status === "success"}
          className={`w-full py-3 px-4 rounded-lg font-medium transition-colors relative ${
            status === "success"
              ? "bg-green-500 hover:bg-green-600"
              : status === "error"
                ? "bg-red-500 hover:bg-red-600"
                : "bg-[#fe5e25] hover:bg-[#e55521]"
          } text-white`}
        >
          {loading ? (
            <Loader2 className="animate-spin mx-auto" size={20} />
          ) : status === "success" ? (
            "Successfully Added! ✓"
          ) : status === "error" ? (
            "Error Joining Waitlist - Try Again"
          ) : (
            "Add me to the waitlist"
          )}
        </button>
        {onCancel && (
          <button
            onClick={onCancel}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-4 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
        )}
      </div>
    </>
  );
};

export default WaitlistInlineForm;
